import React from "react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { RichTextView } from "@/components/cms/RichText";
import { Megaphone, Users, Handshake, LinkIcon, HelpCircle } from "lucide-react";

const CONTAINER = "max-w-6xl mx-auto px-4 sm:px-6 lg:px-8";

export function CalloutBanner({ text }) {
  if (!text) return null;
  return (
    <div className="bg-[#DEE029]/25 border-y border-[#DEE029]/50" data-testid="local-callout">
      <div className={`${CONTAINER} py-3 flex items-center gap-2.5 text-[15px] font-medium text-[#1C4358]`}>
        <Megaphone size={18} className="text-[#9C1E22] shrink-0" /> {text}
      </div>
    </div>
  );
}

export function LocalIntro({ text, city }) {
  if (!text) return null;
  return (
    <section className="py-12 sm:py-14 bg-white" data-testid="local-intro">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <h2 className="text-2xl font-display font-bold text-[#1C4358]">Why this matters in {city}</h2>
        <p className="mt-3 text-[17px] text-[#4B5563] leading-relaxed whitespace-pre-line">{text}</p>
      </div>
    </section>
  );
}

export function LocalTeam({ team }) {
  if (!team || team.length === 0) return null;
  return (
    <section className="py-12 sm:py-16" data-testid="local-team">
      <div className={CONTAINER}>
        <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#9C1E22]"><Users size={14} className="inline mr-1" /> Your local team</p>
        <h2 className="mt-2 text-2xl sm:text-3xl font-display font-bold text-[#1C4358]">People who'll walk beside you</h2>
        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {team.map((m, i) => (
            <div key={i} className="rounded-2xl bg-white border border-[#1C4358]/10 p-5" data-testid={`local-team-member-${i}`}>
              {m.photo_url && <img src={m.photo_url} alt={m.photo_alt || m.name} loading="lazy" className="h-16 w-16 rounded-full object-cover mb-3" />}
              <h3 className="font-display font-semibold text-[#1C4358] text-[17px]">{m.name}</h3>
              {m.role && <p className="text-[13px] text-[#9C1E22] font-medium">{m.role}</p>}
              {m.bio_html
                ? <RichTextView html={m.bio_html} className="mt-2 text-[14px] text-[#4B5563] leading-relaxed" testid={`local-team-bio-${i}`} />
                : (m.bio && <p className="mt-2 text-[14px] text-[#4B5563] leading-relaxed">{m.bio}</p>)}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function LocalPartners({ partners }) {
  if (!partners || partners.length === 0) return null;
  return (
    <section className="py-12 sm:py-16 bg-[#9BB9CE]/12" data-testid="local-partners">
      <div className={CONTAINER}>
        <p className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#9C1E22]"><Handshake size={14} className="inline mr-1" /> Trusted local partners</p>
        <h2 className="mt-2 text-2xl sm:text-3xl font-display font-bold text-[#1C4358]">Vetted help in your community</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {partners.map((p, i) => (
            <div key={i} className="rounded-xl bg-white border border-[#1C4358]/10 p-5" data-testid={`local-partner-${i}`}>
              {(p.logo_url || p.photo_url) && <img src={p.logo_url || p.photo_url} alt={p.logo_alt || p.org_name} loading="lazy" className="h-12 w-auto max-w-[140px] object-contain mb-3" />}
              <h3 className="font-display font-semibold text-[#1C4358] text-[16px]">{p.org_name}</h3>
              {(p.type || p.category) && <p className="text-[13px] text-[#7A8A99]">{[p.type, p.category].filter(Boolean).join(" · ")}</p>}
              {p.blurb_html
                ? <RichTextView html={p.blurb_html} className="mt-2 text-[14px] text-[#4B5563] leading-relaxed" testid={`local-partner-blurb-${i}`} />
                : (p.blurb && <p className="mt-2 text-[14px] text-[#4B5563] leading-relaxed">{p.blurb}</p>)}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function LocalResources({ links }) {
  if (!links || links.length === 0) return null;
  return (
    <section className="py-10 sm:py-12 bg-white" data-testid="local-resources">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <h2 className="text-xl font-display font-bold text-[#1C4358]"><LinkIcon size={16} className="inline mr-1.5 text-[#9C1E22]" /> Helpful local resources</h2>
        <ul className="mt-4 space-y-2">
          {links.map((l, i) => (
            <li key={i}>
              <a href={l.url} target="_blank" rel="noreferrer" data-testid={`local-resource-${i}`} className="text-[15px] text-[#1C4358] underline hover:text-[#9C1E22]">{l.label || l.url}</a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function LocalFaqs({ faqs }) {
  if (!faqs || faqs.length === 0) return null;
  return (
    <section className="py-12 sm:py-14" data-testid="local-faqs">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <h2 className="text-2xl font-display font-bold text-[#1C4358]"><HelpCircle size={18} className="inline mr-1.5 text-[#9C1E22]" /> Local questions</h2>
        <Accordion type="single" collapsible className="mt-4">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`lf-${i}`} className="border-b border-[#1C4358]/12">
              <AccordionTrigger className="text-left text-[17px] font-display font-semibold text-[#1C4358] hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="text-[15px] text-[#4B5563] leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
